import { Link, type LinkProps } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import Skeleton from '@/components/ui/Skeleton'
import { cn } from '@/lib/utils'
import { Line } from '@/screens/app/components/PendingBlocks'

/**
 * The paired panels under Your portfolio (Today's news beside Company announcements), drawn the
 * way the revamp mock draws them: a hairline box on the card fill, a 40px head band tinted like a
 * table's (`bg-table-head`, the ink at 5% over the card) with the title at 14/600 and a quiet
 * link at the right, then rows that carry their own 16px inset so the hairline between them stops
 * short of the box. Mock 41px head → 40 on the 4-grid; its rows are 63–64px, ours 64.
 */
export function HomePanel({
  title,
  action,
  className,
  children,
}: {
  title: string
  /** The head's right end: a `SeeAllLink size="rail"`. */
  action?: ReactNode
  className?: string | undefined
  children: ReactNode
}) {
  return (
    <section className={cn('flex min-w-0 flex-col border border-border bg-card', className)}>
      <div className="flex h-10 shrink-0 items-center justify-between gap-3 border-b border-border bg-table-head px-4">
        <h2 className="min-w-0 truncate text-h-s text-foreground">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  )
}

/** Rows, hairline between, each `PanelRow`'s `<li>` carrying the 16px side inset. */
export function PanelRows({ children }: { children: ReactNode }) {
  return <ul className="flex flex-1 flex-col">{children}</ul>
}

/**
 * One row. The wash is the shared hover wash, bled 16px past the row's own sides so it fills the
 * box edge to edge; keyboard focus gets it too, plus the inset ring.
 */
export function PanelRow({
  to,
  stockSymbol,
  children,
}: {
  to?: NonNullable<LinkProps['to']>
  /** A row wholly about one listed company opens that stock's page. */
  stockSymbol?: string | undefined
  children: ReactNode
}) {
  const body = <span className="relative flex w-full min-w-0 items-center gap-3">{children}</span>
  const row = cn(
    'group/row relative flex min-h-16 items-center py-3 text-left',
    'before:pointer-events-none before:absolute before:inset-y-0 before:-inset-x-4 before:bg-hover-wash before:opacity-0 before:transition-opacity before:duration-fast',
    'hover:before:opacity-100 focus-visible:before:opacity-100',
    'focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring',
  )
  return (
    <li className="mx-4 border-t border-border-subtle first:border-t-0">
      {stockSymbol === undefined ? (
        <Link to={to ?? '/market/update'} className={row}>
          {body}
        </Link>
      ) : (
        <Link to="/stocks/$symbol" params={{ symbol: stockSymbol }} className={row}>
          {body}
        </Link>
      )}
    </li>
  )
}

/** Rows a Home panel draws. */
export const HOME_PANEL_ROW_COUNT = 4

/**
 * The panel's rows before their data, at the loaded rows' box: 64px, the inset and the hairline
 * the same, a 20px title bar over a 16px meta bar. `leading` adds the announcements' 28px mark.
 */
export function PanelRowsSkeleton({ leading = false }: { leading?: boolean }) {
  return (
    <div className="flex flex-col">
      {Array.from({ length: HOME_PANEL_ROW_COUNT }, (_, index) => (
        <div
          key={index}
          className="mx-4 flex min-h-16 items-center gap-3 border-t border-border-subtle py-3 first:border-t-0"
        >
          {leading ? <Skeleton className="size-7 shrink-0 rounded-full" /> : null}
          <div className="min-w-0 flex-1">
            <Line line="h-5" bar="h-4 w-3/4" />
            <Line line="mt-1 h-4" bar="h-3 w-40" />
          </div>
        </div>
      ))}
    </div>
  )
}
